<script setup lang="ts">
import { RouterView } from 'vue-router'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import AppShell from '@/components/app-shell'
import AppBootSkeleton from '@/components/app-boot-skeleton'
import LegacyPlayerBridgeFrame from '@/components/legacy-player-bridge-frame'
import LegacyPlayerOverlay from '@/components/legacy-player-overlay'
import { useLegacyPlayerOverlay } from '@/composables/useLegacyPlayerOverlay'
import { buildRouteKeyWithoutLegacyPlayerQuery } from '@/utils/legacy-player'

const ROUTE_CACHE_MAX = 2
const {
  isOpen: isLegacyPlayerOpen,
  isBridgeMode: isLegacyPlayerBridgeMode,
  playerUrl: legacyPlayerUrl,
  iframeLoadKey: legacyPlayerLoadKey,
  close: closeLegacyPlayer,
} = useLegacyPlayerOverlay()

function getRouteCacheKey(route: RouteLocationNormalizedLoaded) {
  if (route.meta.keepAlive === true) {
    return String(route.meta.cacheKey ?? route.name ?? route.path)
  }

  return buildRouteKeyWithoutLegacyPlayerQuery(route)
}

function shouldKeepRouteAlive(route: RouteLocationNormalizedLoaded) {
  return route.meta.keepAlive === true
}

function shouldUseAppShell(route: RouteLocationNormalizedLoaded) {
  return route.meta.appShell !== false
}
</script>

<template>
  <LegacyPlayerBridgeFrame
    v-if="isLegacyPlayerBridgeMode"
    :url="legacyPlayerUrl"
    :load-key="legacyPlayerLoadKey"
  />

  <template v-else>
    <RouterView v-slot="{ Component, route }">
      <Suspense>
        <template #default>
          <div class="app-route-host">
            <AppShell v-if="shouldUseAppShell(route)">
              <KeepAlive v-if="shouldKeepRouteAlive(route)" :max="ROUTE_CACHE_MAX">
                <component :is="Component" :key="getRouteCacheKey(route)" />
              </KeepAlive>
              <component v-else :is="Component" :key="getRouteCacheKey(route)" />
            </AppShell>

            <template v-else>
              <KeepAlive v-if="shouldKeepRouteAlive(route)" :max="ROUTE_CACHE_MAX">
                <component :is="Component" :key="getRouteCacheKey(route)" />
              </KeepAlive>
              <component v-else :is="Component" :key="getRouteCacheKey(route)" />
            </template>
          </div>
        </template>
        <template #fallback>
          <AppBootSkeleton />
        </template>
      </Suspense>
    </RouterView>

    <LegacyPlayerOverlay
      :open="isLegacyPlayerOpen"
      :url="legacyPlayerUrl"
      :load-key="legacyPlayerLoadKey"
      @close="closeLegacyPlayer"
    />
  </template>
</template>

<style scoped>
.app-route-host {
  display: contents;
}
</style>
